import type {
  SupportGroup,
  SupportGroupKey,
  SupportOptionsResponse,
  SupportReason,
} from '@/types/Support';

/**
 * Loads `GET /support/options` and narrows it to a single support group.
 *
 * The endpoint is public (no bearer token needed) and its shape drives the form:
 * reasons and their required references come from here, never from the client.
 *
 * @example
 * const { group, reasonItems } = await useSupportOptions('tradel_platform');
 */
export async function useSupportOptions(groupKey: SupportGroupKey) {
  const { data, pending, error, refresh } =
    await useFetchData<SupportOptionsResponse>('support/options', {
      key: `support-options-${groupKey}`,
      method: 'get',
    });

  const options = computed(
    () => data.value as unknown as SupportOptionsResponse | null
  );

  const group = computed<SupportGroup | undefined>(
    () => options.value?.data?.[groupKey]
  );

  const reasons = computed<SupportReason[]>(() => group.value?.reasons ?? []);

  /** `v-select` items for the reason picker. */
  const reasonItems = computed(() =>
    reasons.value.map(({ value, label }) => ({ title: label, value }))
  );

  const findReason = (value: string) =>
    reasons.value.find((reason) => reason.value === value);

  /** References the API says this reason must be submitted with. */
  const requirementsFor = (value: string) => findReason(value)?.requires ?? [];

  return {
    pending,
    error,
    refresh,
    group,
    reasons,
    reasonItems,
    findReason,
    requirementsFor,
  };
}
